import { KeyRound, ListChecks, UserCheck, UserPlus, Users, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useT } from '../../../i18n';
import { useAutomationCan } from '../hooks';
import { CheckTab } from './CheckTab';
import { OtpTab } from './OtpTab';
import { PoolTab } from './PoolTab';
import { ReadinessTab } from './ReadinessTab';
import { RegisterTab } from './RegisterTab';
import { TabBar } from './shared';

/**
 * TestRun's test accounts (TestRun keeps them; the browser calls it with a TMS
 * token): the team's pool, «ready for tests», checking an account on the
 * stand, registering a new client, and the OTP for a manual test. A Viewer
 * reads the pool, readiness and the registration list; checking, OTP and
 * every write are for QA and Admin.
 */

type Tab = 'pool' | 'readiness' | 'check' | 'register' | 'otp';

const TABS: { id: Tab; icon: LucideIcon; write: boolean }[] = [
  { id: 'pool', icon: Users, write: false },
  { id: 'readiness', icon: ListChecks, write: false },
  { id: 'check', icon: UserCheck, write: true },
  { id: 'register', icon: UserPlus, write: false },
  { id: 'otp', icon: KeyRound, write: true },
];

export function AccountsPage() {
  const { t } = useT();
  const can = useAutomationCan();
  const [tab, setTab] = useState<Tab>('pool');
  const tabs = TABS.filter((x) => can.run || !x.write).map((x) => ({
    id: x.id,
    icon: x.icon,
    label: t(`automation.accounts.tab.${x.id}`),
  }));
  // a write tab the role lost (or does not have yet) falls back to the pool
  const current = tabs.some((x) => x.id === tab) ? tab : 'pool';

  return (
    <>
      <PageHeader
        title={t('automation.accounts.title')}
        description={t('automation.accounts.subtitle')}
      />
      <TabBar
        tabs={tabs}
        value={current}
        onChange={setTab}
        label={t('automation.accounts.title')}
      />
      {current === 'pool' ? (
        <PoolTab onCheckTab={can.run ? () => setTab('check') : undefined} />
      ) : current === 'readiness' ? (
        <ReadinessTab />
      ) : current === 'check' ? (
        <CheckTab />
      ) : current === 'register' ? (
        <RegisterTab />
      ) : (
        <OtpTab />
      )}
    </>
  );
}
